import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { RequireAuth } from "./auth/RequireAuth";
import { AppLayout } from "./layout/AppLayout";
import { ThemeProvider } from "./theme/ThemeProvider";
import { AuthDisabledPage } from "./pages/AuthDisabledPage";
import { BacktestPage } from "./pages/BacktestPage";
import { CredentialsPage } from "./pages/CredentialsPage";
import { IntelligencePage } from "./pages/IntelligencePage";
import { JournalPage } from "./pages/JournalPage";
import { LiveDashboardPage } from "./pages/LiveDashboardPage";
import { LoginPage } from "./pages/LoginPage";
import { NewsPage } from "./pages/NewsPage";
import { OverviewPage } from "./pages/OverviewPage";
import { PositionsPage } from "./pages/PositionsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SetupPage } from "./pages/SetupPage";
import { TradesPage } from "./pages/TradesPage";
import { TradingAccountPage } from "./pages/TradingAccountPage";
import { TradingConfigPage } from "./pages/TradingConfigPage";
import { TradingViewSetupPage } from "./pages/TradingViewSetupPage";

// ManualPage pulls in react-markdown + remark-gfm + the entire markdown
// AST pipeline (~60 KB gzip). It's a docs page most users open once, so
// we code-split it — the deps only load when /manual is actually visited.
const ManualPage = lazy(() =>
  import("./pages/ManualPage").then((m) => ({ default: m.ManualPage })),
);

function ManualLoading() {
  return (
    <div className="flex h-full items-center justify-center text-sm text-fg-muted">
      loading manual…
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          {/* Public auth flow */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/setup" element={<SetupPage />} />
          <Route path="/auth/disabled" element={<AuthDisabledPage />} />

          {/* Protected portal — everything under here lives in the
              sidebar layout and requires a logged-in user. */}
          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route path="/live" element={<LiveDashboardPage />} />
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/positions" element={<PositionsPage />} />
            <Route path="/trades" element={<TradesPage />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/backtest" element={<BacktestPage />} />
            <Route path="/intelligence" element={<IntelligencePage />} />
            <Route path="/trading-account" element={<TradingAccountPage />} />
            <Route path="/trading-config" element={<TradingConfigPage />} />
            <Route path="/credentials" element={<CredentialsPage />} />
            <Route path="/tradingview" element={<TradingViewSetupPage />} />
            <Route
              path="/manual"
              element={
                <Suspense fallback={<ManualLoading />}>
                  <ManualPage />
                </Suspense>
              }
            />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Root + catch-all → overview (RequireAuth will redirect
              unauthenticated visitors to /login). */}
          <Route path="/" element={<Navigate to="/overview" replace />} />
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
